// Maps a mindex color-scheme key (see SCHEMES in app.jsx) to the
// off-the-shelf Shiki theme used to render code in that scheme.
// Locked per spec §4.h "Scheme → theme mapping".
//
// `cream` may swap to `github-light` post-eyeball if `solarized-light`
// clashes with the cream reader background — see Task 14.
export const SCHEME_TO_SHIKI_THEME = {
  coal:    'github-light',
  paper:   'github-light',
  carbon:  'github-dark-dimmed',
  inverse: 'github-dark-dimmed',
  cream:   'solarized-light',
};

// The deduplicated list of themes that Shiki must preload. Computed
// from the map so the two stay in lockstep.
export const SHIKI_THEMES = Array.from(new Set(Object.values(SCHEME_TO_SHIKI_THEME)));

// Languages bundled into the same-origin shiki-runtime.js chunk. These
// are the fence labels (and aliases) that ship with the engine — they
// resolve synchronously once the runtime chunk has loaded, no extra
// network round-trip required.
//
// Bias: languages that show up in the user's vault and the mindex
// codebase. ~20 grammars after alias dedupe (see SHIKI_BUNDLED_BASE
// below). Aliases (`ts`, `js`, `sh`, `md`, `py`, etc.) are included so
// common fence labels resolve without surprises.
//
// Anything NOT in this list falls through to a per-language lazy load
// from `/shiki-langs/<id>.js` (see SHIKI_LAZY_BASE for the build
// manifest of supported lazy targets). If a fence label matches no
// bundled language and no lazy target either, the highlight() helper
// renders plain `<pre><code>` and the post-processor still picks the
// language label up.
export const SHIKI_BUNDLED_BASE = [
  // Core script languages (the heaviest grammars, but the most-used).
  'javascript', 'typescript', 'tsx', 'jsx',
  // Data / config formats.
  'json', 'yaml', 'toml', 'ini',
  // Shells (PowerShell first because Windows is the host).
  'powershell', 'bash', 'shellscript',
  // Other common.
  'python', 'sql', 'markdown', 'html', 'css',
  'rust', 'go',
  'diff', 'docker',
];

// Aliases the runtime accepts in addition to the canonical IDs above.
// Mirrors Shiki's own `bundledLanguagesAlias` for the BUNDLED_BASE
// subset. We keep this map explicit (rather than reading it off
// shiki/langs at build time) so the bundle stays decoupled from the
// upstream package's discovery code.
export const SHIKI_BUNDLED_ALIASES = {
  js: 'javascript',
  ts: 'typescript',
  sh: 'bash',
  shell: 'bash',
  zsh: 'bash',
  md: 'markdown',
  py: 'python',
  rs: 'rust',
  yml: 'yaml',
  dockerfile: 'docker',
};

// The full list of fence labels that resolve from the bundled chunk
// without an extra network round-trip. Computed from BASE + ALIASES so
// the two stay in lockstep.
export const SHIKI_BUNDLED_LANGS = [
  ...SHIKI_BUNDLED_BASE,
  ...Object.keys(SHIKI_BUNDLED_ALIASES),
];

// Extra languages we ship as same-origin lazy chunks (one chunk per
// grammar at `/shiki-langs/<id>.js`). The build script emits an entry
// per id below. Add ids here and they are picked up automatically on
// the next build.
//
// Anything outside both BUNDLED_LANGS and LAZY_BASE renders as plain
// `<pre><code>` (no error toast, no console noise — the lang label
// still appears in the meta row).
export const SHIKI_LAZY_BASE = [
  // Niche-but-common languages we want highlighting for, but not in
  // the eager bundle.
  'java', 'csharp', 'cpp', 'c', 'php', 'ruby', 'kotlin', 'swift',
  'scala', 'elixir', 'haskell', 'lua', 'r', 'dart', 'zig', 'nim',
  'protobuf', 'graphql', 'nginx',
];

// Aliases for lazy languages. The runtime resolves alias → canonical id,
// then dynamic-imports `/shiki-langs/<id>.js`.
export const SHIKI_LAZY_ALIASES = {
  cs: 'csharp',
  'c++': 'cpp',
  rb: 'ruby',
  kt: 'kotlin',
  proto: 'protobuf',
};

// Resolves a fence label (alias or canonical) to one of:
//   { kind: 'bundled', id }   — synchronously available once shiki-runtime.js has loaded
//   { kind: 'lazy',    id }   — dynamic-import `/shiki-langs/<id>.js`
//   null                       — unsupported, fall through to plain <pre><code>
export function resolveLang(rawLang) {
  if (!rawLang) return null;
  const lang = String(rawLang).toLowerCase();
  if (SHIKI_BUNDLED_BASE.includes(lang)) return { kind: 'bundled', id: lang };
  if (SHIKI_BUNDLED_ALIASES[lang]) return { kind: 'bundled', id: SHIKI_BUNDLED_ALIASES[lang] };
  if (SHIKI_LAZY_BASE.includes(lang)) return { kind: 'lazy', id: lang };
  if (SHIKI_LAZY_ALIASES[lang]) return { kind: 'lazy', id: SHIKI_LAZY_ALIASES[lang] };
  return null;
}

export function getShikiTheme(scheme) {
  return SCHEME_TO_SHIKI_THEME[scheme] || 'github-light';
}

// Back-compat alias for callers still on the pre-chunking import. The
// markdown-pipeline switches to `resolveLang` in a follow-up commit;
// this re-export keeps the build green in the meantime.
export const SHIKI_LANGS = SHIKI_BUNDLED_LANGS;

